<script>
    import { onMount, beforeUpdate } from "svelte";
    import { Chart, registerables } from "chart.js";
    import annotationPlugin from "chartjs-plugin-annotation";
    import { theme } from "$stores/themeStore";
    import { get } from 'svelte/store';

    export let data = [];

    let canvas;
    let chart;
    let chartBorderColor = "rgba(75, 192, 192, 1)";
    let chartBackgroundColor = "rgba(75, 192, 192, 0.2)";

    // Number of days to display (adjust to fit what's visible in the viewport)
    const daysToShow = 365;
    
    // Calculate the width of the canvas based on the fixed number of days
    const barWidth = 8; // Width of each bar in pixels
    const canvasWidth = daysToShow * barWidth; // Total canvas width for scrolling

    import { studyProperties } from '$stores/configStore';
    // Target is 80% of expected per day from study interval
    $: expectedPerDay = Math.round(1440 / (($studyProperties?.glucoseParameters?.cgmIntervalMinutes ?? 5)));
    $: targetValue = Math.round(expectedPerDay * 0.8);

    const lightModeOptions = {
        responsive: false,
        maintainAspectRatio: false,
        scales: {
            x: {
                title: {
                    display: false,
                    text: "Date",
                    color: "black",
                },
                ticks: {
                    color: "black",
                    maxRotation: 45,
                    minRotation: 45,
                    autoSkip: false, // Don't auto-skip to show more labels
                    callback: function(value, index) {
                        // Show every 3rd label
                        return index % 3 === 0 ? this.getLabelForValue(value) : '';
                    }
                },
                grid: {
                    display: false
                },
                offset: true
            },
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: "Count",
                    color: "black",
                },
                position: "left",
                ticks: {
                    color: "black",
                },
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    title: function(tooltipItems) {
                        return tooltipItems[0].label;
                    },
                    label: function(context) {
                        return `Readings: ${context.raw || 0}`;
                    }
                }
            },
            annotation: {
                annotations: {
                    line1: {
                        type: "line",
                        yMin: targetValue,
                        yMax: targetValue,
                        borderColor: "black",
                        borderWidth: 0.5,
                        label: {
                            content: `Target: ${targetValue}`,
                            display: true,
                            position: "end",
                            backgroundColor: "rgba(0,0,0,0.7)",
                            font: {
                                size: 10
                            }
                        }
                    },
                },
            },
        },
    };

    const darkModeOptions = {
        responsive: false,
        maintainAspectRatio: false,
        scales: {
            x: {
                title: {
                    display: false,
                    text: "Date",
                    color: "white",
                },
                ticks: {
                    color: "white",
                    maxRotation: 45,
                    minRotation: 45,
                    autoSkip: false, // Don't auto-skip to show more labels
                    callback: function(value, index) {
                        // Show every 3rd label
                        return index % 3 === 0 ? this.getLabelForValue(value) : '';
                    }
                },
                grid: {
                    display: false
                },
                offset: true
            },
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: "Count",
                    color: "white",
                },
                position: "left",
                ticks: {
                    color: "white",
                },
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    title: function(tooltipItems) {
                        return tooltipItems[0].label;
                    },
                    label: function(context) {
                        return `Readings: ${context.raw || 0}`;
                    }
                }
            },
            annotation: {
                annotations: {
                    line1: {
                        type: "line",
                        yMin: targetValue,
                        yMax: targetValue,
                        borderColor: "rgba(110, 210, 170, 0.8)",
                        borderWidth: 0.5,
                        label: {
                            content: `Target: ${targetValue}`,
                            display: true,
                            position: "end",
                            backgroundColor: "rgba(33,33,33,0.7)",
                            color: "white",
                            font: {
                                size: 10
                            }
                        }
                    },
                },
            },
        },
    };

    // Function to format date as YYYY-MM-DD
    function formatDate(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    // Generate an array of the last 365 days (starting from tomorrow)
    function generateDaysArray() {
        const daysArray = [];
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        
        for (let i = 0; i < daysToShow; i++) {
            const date = new Date(tomorrow);
            date.setDate(tomorrow.getDate() - i);
            daysArray.push(formatDate(date));
        }
        
        // Return in descending order (newest first, like the hourly chart)
        return daysArray;
    }

    // Format date for display as MM/DD (e.g., 10/27)
    function formatDisplayDate(dateStr) {
        const parts = dateStr.split('-');
        const date = new Date(parts[0], parts[1] - 1, parts[2]);
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${mm}/${dd}`;
    }

    onMount(() => {
        // Initialize chart even if there's no data
        Chart.register(...registerables, annotationPlugin);
        createChart();
        
        // Log the data we received
        if (Array.isArray(data)) {
            console.log(`DailyBarChart mounted with ${data.length} days of data`);
        }
    });

    beforeUpdate(() => {
        if (chart) {
            chart.destroy();
            chartBackgroundColor = get(theme) === "dark" ? "rgba(13, 148, 136, 0.2)" : "rgba(190, 24, 123, 0.2)";
            chartBorderColor = get(theme) === "dark" ? "rgba(13, 148, 136, 1)" : "rgba(190, 24, 123, 1)";   
            createChart();
        }
    });

    $: if (chart) {
        chart.destroy();
        chartBackgroundColor = get(theme) === "dark" ? "rgba(13, 148, 136, 0.2)" : "rgba(190, 24, 123, 0.2)";
        chartBorderColor = get(theme) === "dark" ? "rgba(13, 148, 136, 1)" : "rgba(190, 24, 123, 1)";   
        createChart();
    }

    function createChart() {
        if (!canvas) return;
        
        const currentTheme = get(theme); // Get the current theme
        const options = currentTheme === "dark" ? darkModeOptions : lightModeOptions;
        const ctx = canvas.getContext("2d");
        
        // Get data from the actual dataset first
        let actualDataDays = [];
        let dataMap = {};
        
        if (Array.isArray(data)) {
            // Get all actual days we have data for
            data.forEach(item => {
                if (item.day) {
                    const value = (item.totalCount ?? item.count ?? 0);
                    actualDataDays.push(item.day);
                    dataMap[item.day] = value;
                }
            });
            console.log(`Chart received ${data.length} compliance data points`);
        }
        
        // Generate all 365 days for the chart
        let allDays = generateDaysArray();
        
        // Create data array from our days, inserting actual data where available
        const chartData = allDays.map(day => {
            return dataMap[day] || 0;
        });
        
        // Format display labels (shorter for space)
        const displayLabels = allDays.map(day => formatDisplayDate(day));
        
        // Use a consistent color for all bars
        const mainColor = currentTheme === "dark" ? 
            "rgba(13, 148, 136, 0.7)" : 
            "rgba(190, 24, 123, 0.7)";
            
        const sundayColor = currentTheme === "dark" ? 
            "rgba(13, 148, 236, 0.7)" : 
            "rgba(190, 24, 223, 0.7)";
        
        const backgroundColors = allDays.map((day) => {
            // Parse the day string (format "YYYY-MM-DD") to get Date object
            const parts = day.split('-');
            const date = new Date(parts[0], parts[1] - 1, parts[2]);
            
            // Only differentiate Sundays
            const isDataAvailable = dataMap[day] && dataMap[day] > 0;
            if (!isDataAvailable) {
                return currentTheme === "dark" ? 
                    "rgba(13, 148, 136, 0.1)" : 
                    "rgba(190, 24, 123, 0.1)";
            }
            
            // Sunday gets a special color, all other days get the same color
            return date.getDay() === 0 ? sundayColor : mainColor;
        });

        // Inject dynamic annotation line bounds so it draws horizontally across all days
        if (options?.plugins?.annotation?.annotations?.line1) {
            options.plugins.annotation.annotations.line1.yMin = targetValue;
            options.plugins.annotation.annotations.line1.yMax = targetValue;
            options.plugins.annotation.annotations.line1.label.content = `Target: ${targetValue}`;
            options.plugins.annotation.annotations.line1.xMin = 0;
            options.plugins.annotation.annotations.line1.xMax = allDays.length - 1;
        }

        chart = new Chart(ctx, {
            type: "bar",
            data: {
                labels: displayLabels,
                datasets: [
                    {
                        label: "Readings",
                        data: chartData,
                        backgroundColor: backgroundColors,
                        borderColor: backgroundColors,
                        borderWidth: 1,
                    },
                ],
            },
            options: options,
        });
    }

    theme.subscribe(() => {
        if (chart) {
            chart.destroy();
            chartBackgroundColor = get(theme) === "dark" ? "rgba(13, 148, 136, 0.2)" : "rgba(190, 24, 123, 0.2)";
            chartBorderColor = get(theme) === "dark" ? "rgba(13, 148, 136, 1)" : "rgba(190, 24, 123, 1)";   
            createChart();
        }
    });
</script>

<div style="overflow-x: auto; width: 100%; height: 250px; scrollbar-width: none; -ms-overflow-style: none;">
    <style>
        div::-webkit-scrollbar {
            display: none;
        }
    </style>
    <canvas bind:this={canvas} width={canvasWidth} height="250"></canvas>
</div> 